//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';

import { SamplingCatalogProduct } from '@apis/campaign-requests/sampling/products/samplingProductsApiV2Model';
import { IdentifierRow } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/shared/products-section/catalog-search-modal/IdentifierRow';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { sanitizeProductDescriptionHtml } from '@campaigns/utils/sampling/productDescriptionUtils';
import { DefaultButton } from '@components/atoms/button/Button';
import { ImageWithErrorHandling } from '@components/atoms/image-with-error-handling/ImageWithErrorHandling';
import {
  CaptionSecondaryText,
  CaptionText,
  SubsectionText,
} from '@components/atoms/text/Text';
import { Mixins } from '@theme/mixins';

const VISIBLE_IDENTIFIER_COUNT = 4;

interface CatalogMatchCardProps {
  onSelectProduct: (product: SamplingCatalogProduct) => void;
  product: SamplingCatalogProduct;
}

export const CatalogMatchCard = ({
  onSelectProduct,
  product,
}: CatalogMatchCardProps) => {
  const descriptionHtml = useMemo(
    () => sanitizeProductDescriptionHtml(product.description),
    [product.description],
  );
  const brandAndCategory = [
    { field: 'brand', value: product.brand },
    { field: 'category', value: product.category },
  ].filter(({ value }) => Boolean(value));

  return (
    <Card>
      <SelectButton
        onClick={() => onSelectProduct(product)}
        trackingId={
          SAMPLING_CAMPAIGNS_TRACKING_ID.CAMPAIGN_REQUEST_CATALOG_PRODUCT_SELECT_BUTTON
        }
      >
        Select Product
      </SelectButton>
      <ProductInfo>
        <ProductImage
          alt={product.name ?? 'product image'}
          src={product.imageUrl}
        />
        <ProductDetails>
          <div>
            {product.name && (
              <SubsectionText ellipsize={1}>{product.name}</SubsectionText>
            )}
            {brandAndCategory.length > 0 && (
              <BrandAndCategory>
                {brandAndCategory.map(({ field, value }) => (
                  <CaptionSecondaryText key={`${field}-${value}`}>
                    {value}
                  </CaptionSecondaryText>
                ))}
              </BrandAndCategory>
            )}
          </div>
          <ProductMeta>
            {descriptionHtml && (
              <DescriptionText
                dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                data-testid='catalog-match-description'
                ellipsize={2}
              />
            )}
            <IdentifierRow
              identifiers={product.upcs}
              label='UPC'
              visibleCount={VISIBLE_IDENTIFIER_COUNT}
            />
            <IdentifierRow
              identifiers={product.eans}
              label='EAN'
              visibleCount={VISIBLE_IDENTIFIER_COUNT}
            />
          </ProductMeta>
        </ProductDetails>
      </ProductInfo>
    </Card>
  );
};

const Card = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[2]};
    margin-top: ${theme.spacings[1]};
    padding: ${theme.spacings[2]};
    background-color: ${theme.colors.cyan0};
    border: ${theme.borderWidths[1]} solid ${theme.colors.cyan8};
    border-radius: ${theme.echo.borderRadius.sm};
  `,
);

const SelectButton = styled(DefaultButton)`
  width: 100%;
`;

const ProductInfo = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacings[2]};
`;

const ProductImage = styled(ImageWithErrorHandling)(
  ({ theme }) => css`
    flex-shrink: 0;
    width: 100px;
    height: 100px;
    object-fit: cover;
    background-color: ${theme.colors.gray0};
    border-radius: ${theme.echo.borderRadius.sm};
  `,
);

const ProductDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[1]};
  min-width: 0;
  flex: 1;
`;

const BrandAndCategory = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacings[2]};

    > * + * {
      padding-left: ${theme.spacings[2]};
      border-left: ${theme.borderWidths[1]} solid ${theme.colors.gray2};
    }
  `,
);

const ProductMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[2]};
  min-width: 0;
`;

const DescriptionText = styled(CaptionText)`
  p,
  ul,
  ol,
  li {
    display: inline;
    margin: 0;
    padding: 0;
  }

  li + li::before {
    content: ' \\2022 ';
  }

  a {
    ${Mixins.Text.LinkCaptionPrimary};
  }
`;
